import axios from "axios";
import { resolveApiUrl } from "../assets/GlobalVariables";
import { getSessionId } from "./authService";

const RECENT_SEARCHES_PATH = "/api/agent/recent-searches";

export const RECENT_SEARCH_LIMIT_AGENT = 10;
export const RECENT_SEARCH_LIMIT_ADMIN = 100;

const HR_RECENT_SAVED_EVENT = "hr-agent-recent-search-saved";

/** Dedupe accidental double-fires (StrictMode / rapid re-entry) at the send layer only. */
const _recentSaveDedup = { key: "", at: 0 };
const RECENT_SAVE_DEDUP_MS = 3200;

function stableRecentSaveKey(body) {
  try {
    const b = body && typeof body === "object" ? body : {};
    return JSON.stringify({
      category: b.category,
      destination_label: b.destination_label,
      destination_code: b.destination_code,
      departure_date: b.departure_date,
      return_date: b.return_date,
      nights: b.nights,
      adults: b.adults,
      children: b.children,
      infants: b.infants,
      rooms_count: b.rooms_count,
      raw_payload: b.raw_payload,
    });
  } catch (_) {
    return `${Date.now()}:${Math.random()}`;
  }
}

function recentSearchesUrl() {
  return resolveApiUrl(RECENT_SEARCHES_PATH);
}

export function shouldRecordAgentDashboardRecentSearch() {
  if (typeof window === "undefined") return false;
  const p = window.location.pathname || "";
  return p === "/p/dashboard/searchoffer" || p.startsWith("/p/dashboard/searchoffer/");
}

/**
 * Persist one recent search snapshot (requires agent session cookie / X-Session-ID).
 * Pass `allowAnyPath: true` for non-dashboard flows (e.g. campaign landing pages).
 */
export async function persistAgentRecentSearch(body, { allowAnyPath = false } = {}) {
  if (!allowAnyPath && !shouldRecordAgentDashboardRecentSearch()) return;

  let sessionID;
  try {
    sessionID = await getSessionId();
  } catch (_) {
    return;
  }
  if (!sessionID || !String(sessionID).trim()) return;

  const dedupKey = stableRecentSaveKey(body);
  const now = Date.now();
  if (
    dedupKey === _recentSaveDedup.key &&
    now - _recentSaveDedup.at < RECENT_SAVE_DEDUP_MS
  ) {
    if (import.meta.env.DEV) {
      console.info("[RECENT_SEARCH_POST_DEDUP_SKIP]", {
        category: body?.category,
      });
    }
    return;
  }
  _recentSaveDedup.key = dedupKey;
  _recentSaveDedup.at = now;

  if (import.meta.env.DEV) {
    console.info(
      "[RECENT_SEARCH_POST_ATTEMPT]",
      JSON.stringify({ category: body?.category }),
    );
  }

  try {
    await axios.post(recentSearchesUrl(), body || {}, {
      headers: {
        "Content-Type": "application/json",
        "X-Session-ID": String(sessionID).trim(),
      },
      withCredentials: true,
      timeout: 12000,
    });
    if (import.meta.env.DEV) {
      console.info("[RECENT_SEARCH_POST_SUCCESS]", {
        category: body?.category,
      });
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(HR_RECENT_SAVED_EVENT));
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[RECENT_SEARCH_POST_FAIL]", {
        status: err?.response?.status,
        category: body?.category,
        message: err?.message,
      });
    }
  }
}

/** @returns {Promise<{items: Array}>} */
export async function fetchAgentRecentSearches(limit = 10) {
  const sessionID = await getSessionId();
  if (!sessionID || !String(sessionID).trim()) {
    return { items: [] };
  }
  const sid = String(sessionID).trim();
  const { data } = await axios.get(recentSearchesUrl(), {
    params: { limit },
    headers: { "X-Session-ID": sid },
    withCredentials: true,
    timeout: 20000,
  });
  const items = Array.isArray(data?.items) ? data.items : [];
  if (import.meta.env.DEV) {
    console.info("[RECENT_SEARCH_GLOBAL_FETCH]", { count: items.length });
  }
  return { items };
}

export const RECENT_SEARCH_SAVED_EVENT = HR_RECENT_SAVED_EVENT;
